import { AppointmentServiceDao } from '../db-models/appointment-service-dao';
import { EventDateDao } from '../db-models/event-date-dao';
import { AppointmentsSlotsDao } from '../db-models/free-appointment-dao';
import { StoresDao } from '../db-models/stores-dao';
import { TaxableLine } from '../services/vat-calculation.util';
import { EventDao } from './../db-models/event-dao';

export interface CartItem {
  appointmentDaoObject: AppointmentsSlotsDao;
  storeId: number;
  storeName: string;
  store: StoresDao;
  date: Date;
  startTime: string;
  shortStart: number;
  endTime: string;
  workerId: number;
  servicesIds: number[];
  cartItemId: string;
  workerName: string;
  workerEmail: string;
  total: CartItemTotal;
  relatedServiceIds: number[];
  relatedServices: AppointmentServiceDao[];
  relatedAppointmentCardId: number;
  referenceServices: Array<CartItemReferenceService>;
  parentServiceId: number;
  location?: string;
  workerUuids: string[];
  reservation_start_time?: Date;
  combiServiceWorkersMapping: Record<number, number>;
}

export interface CartItemReferenceService {
  serviceId: number;
  serviceName: string;
  serviceCategoryId: number;
}

export interface CartItemTotal {
  services?: object[];
  totalDuration?: number;
  totalPrice?: number;
  tax?: number;
  isPriceFromActivated?: number;
  duration?: number;
  price?: any;
  taxPrice?: any;
  /** One entry per taxable service line (price > 0, tax rate > 0) — feeds `calculateVat`. */
  taxableLines?: TaxableLine[];
}

export interface AppointmentBookItem {
  partnerId: number;
  services: string;
  worker_id?: number;
  worker_uuids?: string[];
  combi_workers?: Record<number, number>;
  start: string;
  end: string;
  gender: string;
  title: number,
  prename?: string;
  lastname?: string;
  captcha?: string;
  friendly_captcha?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  birthday?: string;
  company_name?: string;
  comment?: string;
  internal_comment?: string;
  customer_id?: number;
  customer_uuid?: string;
  customFieldValues: {
    custom_field_id: number;
    type: string;
    value?: any;
    file_ids?: any;
  }[];
  street?: string;
  zip?: string;
  city?: string;
  country: number,
  create_account?: boolean;
  user_selected_language?: string;
  user_browser_language?: string;
  // TODO STRIPE_MIGRATION:
  // stripe_payment_method_id: string;
  stripe_payment_intent_id?: string;
  coupon_code?: string;
  payment_type?: string;
  // Standalone "Invoice" payment mode — frozen contract (comment 358802). Present
  // only when payment_type === external_invoice AND the "different address" checkbox was
  // checked; omitted otherwise (the backend snapshots the booking-contact address).
  invoice_address?: {
    company_name?: string;
    prename: string;
    lastname: string;
    street: string;
    street_number: string;
    zip: string;
    city: string;
    country_id?: number;
    email: string;
    phone?: string;
  };
  booker_worker_id?: number;
  customer_notification_preference?: string;
  meeting_type_id?: number;
  utm_source?: string;
  utm_content?: string;
  utm_term?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  paypal_order_id?: string;
  paypal_payer_id?: string;
  paypal_raw_data?: string;
  paypal_status?: string;
  paypal_capture_id?: string;
  paypal_payment_log_id?: string;
  saferpay_transaction_id?: string;
  saferpay_payment_method?: string;
  /** Adyen: opaque handles only — the backend reads the real outcome from Adyen. */
  adyen_session_id?: string;
  adyen_session_result?: string;
  broker_prename?: string;
  broker_lastname?: string;
  broker_email?: string;
  broker_company?: string;
  broker_phone?: string;
  broker_mobile?: string;
  broker_street?: string;
  broker_zip?: string;
  broker_city?: string;
  token: string;
  location?: string;
  is_internal?: boolean;
  appointment_source_id: number;
  amount?: number;
  timezone?: string;
  accept_data_policy?: number;

  // conference related properties
  booking_link_uuid?: string;

  // client side property
  referenceServices?: Array<CartItemReferenceService>;
  resource_name?: string;
  store_id?: number;
  store_name?: string;
  worker_email?: string;
}

export interface EventCartItem {
  slotId: number;
  workerId: number;
  date: Date;
  startTime: Date;
  endTime: Date;
  duration: number;
  currentEvent: CurrentEvent;
  eventId: string | number;
  category: string;
  tempSlotOptions?: number[];
  tempSelectLabel?: string;
  tempSelectOptionLabel?: string;
  selectedSlot?: { slot_id: number; amount: number };
  is_waitinglist: boolean;
  is_waitinglist_enabled: boolean;
  regular_booking_count?: number;
  regular_capacity?: number;
  waitinglist_booking_count?: number;
  waitinglist_capacity?: number;
  available_regular_capacity?: number;
  available_waitinglist_capacity?: number;
  referenceEvent?: EventDao;
  childSlots?: EventDateDao[];
  location?: string;
  is_widget_event_guest_email_required?: number;
  is_widget_event_guest_phone_required?: number;
  meeting_type_id?: number;
}

export interface CurrentEvent {
  title: string;
  description: string;
  place: string;
  image: string;
  price: number;
  tax: number;
  taxRate: number;
}

export interface EventBookItem {
  event_id: string | number;
  event_name: string;
  category: string;
  slot_id: number;
  gender: string;
  title: number;
  prename: string;
  lastname: string;
  email: string;
  phone: string;
  mobile: string;
  birthday?: string;
  customer_notification_preference: string;
  customer_id?: number;
  customer_uuid?: string;
  customFieldValues: {
    custom_field_id: number;
    type: string;
    value?: any;
    file_ids?: any;
  }[];
  street?: string;
  zip?: string;
  city?: string;
  country: number;
  create_account?: boolean;
  user_selected_language?: string;
  user_browser_language?: string;
  company_name?: string;
  comment?: string;
  internal_comment?: string;
  guests?: GuestModel[];
  anonymous_guest_count?: number;
  stripe_payment_intent_id?: string;
  childSlots?: { slot_id: number; amount: number }[];
  is_waitinglist: boolean;
  coupon_code?: string;
  payment_type?: string;
  utm_source?: string;
  utm_content?: string;
  utm_term?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  paypal_order_id?: string;
  paypal_payer_id?: string;
  paypal_raw_data?: string;
  paypal_status?: string;
  paypal_capture_id?: string;
  paypal_payment_log_id?: string;
  appointment_source_id: number;
  meeting_type_id?: number;
  saferpay_transaction_id?: string;
  saferpay_payment_method?: string;
  /** Adyen: opaque handles only — the backend reads the real outcome from Adyen. */
  adyen_session_id?: string;
  adyen_session_result?: string;
  worker_id?: number;
  friendly_captcha?: string;
  amount?: number;
  timezone?: string;
  accept_data_policy?: number;
}

export interface ResourceCartItem {
  resourceId: number;
  startDate: Date;
  endDate: Date;
  startTime: string;
  endTime: string;
  resourceDetails: object;
}

export interface ResourceBookItem {
  resourceId: number;
  start: string;
  end: string;
  prename: string;
  lastname: string;
  email: string;
  phone: string;
  comment: string;
  customer_id?: number;
  customer_uuid?: string;
  customFieldValues: { custom_field_id: number; value: string }[];
  street?: string;
  zip?: string;
  city?: string;
  create_account?: boolean;
}

export class GuestModel {
  prename: string;
  lastname: string;
  email?: string;
  phone?: string;
}
